import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  setUpdateInputRows,
  saveAssessment,
  setAction,
  setGaze,
  setFacialSymmetry,
  setSpeech,
  setLimbMotorStrength,
  setLimbSensation,
  setSuspectedInfection,
  setSepsisIndicators,
  setPossibleSourceOfInfection,
  setCustomFieldOption,
} from '../store/assessment.actions'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { AssessmentConstants } from '../store/assessment.constant'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import { getAssessmentSelector } from '../selectors'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import Procedure from '../../../../components/procedure/Procedure.component'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialAssessmentProcedureState } from '../store/assessment.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { goBack } from '../../../../utilities/navigation.util'
import { IOption, IOptions } from '../../../../common/common.types'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { getAssessmentOptionsSelector, getGenericOptionsSelector } from '../../../options/selectors'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { getCrewListSelector } from '../../../crew/selectors'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'

type ActionCreator<T> = (payload: T) => { type: string; payload: T }

const Assessment: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const previousPath = location?.location?.state?.from

  const crewList = useSelector(getCrewListSelector)
  const assessment = useSelector(getAssessmentSelector)

  const {
    date,
    time,
    pta,
    assessedBy,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    procedureComplications,
    response,
    responsePNNV,
    action,
    gaze,
    facialSymmetry,
    speech,
    limbMotorStrength,
    limbSensation,
    suspectedInfection,
    sepsisIndicators,
    possibleSourceOfInfection,
    authorization,
    comments,
    updateInputRows,
    _hideNeuroFields,
    _hideSepsisFields,
    customAssessmentsFields,
  } = assessment

  const assessmentOptions = useSelector(getAssessmentOptionsSelector)
  const {
    actionOptions,
    neuroGazeOptions,
    neuroSpeechOptions,
    neuroFacialOptions,
    neuroLimbOptions,
    neuroMotorOptions,
    sepsisIndicatorsOptions,
    customActionFieldsOptions,
    customActionFieldsAnswersOptions,
  } = assessmentOptions

  const genericOptions = useSelector(getGenericOptionsSelector)
  const { yesNoOptions } = genericOptions

  const disableActionField = !!(action?.id && previousPath === '/tabs/timeline')
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'nemsisCode']
    const currentState = filterOutObjectKeys(assessment, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialAssessmentProcedureState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    if (initialStateCheck()) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'assessments/SET_TIME', time: time })
              dispatch({ type: 'assessments/SET_DATE', date: date })
              dispatch(saveAssessment())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: assessment.key,
                rowActionType: TimelineConstants.SET_ASSESSMENT,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      if (initialStateCheck()) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }
  const setActionOnChange = (option: IOption) => {
    dispatch(setAction(option))
  }

  const createChangeHandler = useCallback(
    (action: ActionCreator<IOption | null>) => (value: IOption | null) => {
      dispatch(action(value))
      dispatch(saveAssessment())
    },
    [dispatch]
  )

  const createTextHandler = useCallback(
    (action: ActionCreator<string>) => (e: CustomEvent) => {
      e.preventDefault()
      dispatch(action(e.detail.value))
      dispatch(saveAssessment())
    },
    [dispatch]
  )

  const staticCustomRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_ACTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('assessment.action'),
            dataTestID: 'Action',
            listHeader: t('assessment.action'),
            options: actionOptions,
            action: setActionOnChange,
            selectedOption: action,
            disabled: disableActionField,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_GAZE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideNeuroFields,
          props: {
            label: t('assessment.gaze'),
            dataTestID: 'Gaze',
            listHeader: t('assessment.gaze'),
            options: neuroGazeOptions,
            action: createChangeHandler(setGaze),
            selectedOption: gaze,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_FACIAL_SYMMETRY,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideNeuroFields,
          props: {
            label: t('assessment.facialSymmetry'),
            dataTestID: 'facialSymmetry',
            listHeader: t('assessment.facialSymmetry'),
            options: neuroFacialOptions,
            action: createChangeHandler(setFacialSymmetry),
            selectedOption: facialSymmetry,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_SPEECH,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideNeuroFields,
          props: {
            label: t('assessment.speech'),
            dataTestID: 'Speech',
            listHeader: t('assessment.speech'),
            options: neuroSpeechOptions,
            action: createChangeHandler(setSpeech),
            selectedOption: speech,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_LIMB_MOTOR_STRENGTH,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideNeuroFields,
          props: {
            label: t('assessment.limbMotorStrength'),
            dataTestID: 'Limb Motor Strength',
            listHeader: t('assessment.limbMotorStrength'),
            options: neuroMotorOptions,
            action: createChangeHandler(setLimbMotorStrength),
            selectedOption: limbMotorStrength,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_LIMB_SENSATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideNeuroFields,
          props: {
            label: t('assessment.limbSensation'),
            dataTestID: 'Limb Sensation',
            listHeader: t('assessment.limbSensation'),
            options: neuroLimbOptions,
            action: createChangeHandler(setLimbSensation),
            selectedOption: limbSensation,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_SUSPECTED_INFECTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideSepsisFields,
          props: {
            label: t('assessment.suspectedInfection'),
            dataTestID: 'Suspected Infection',
            listHeader: t('assessment.suspectedInfection'),
            options: yesNoOptions,
            action: createChangeHandler(setSuspectedInfection),
            selectedOption: suspectedInfection,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_SEPSIS_INDICATORS,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: _hideSepsisFields,
          props: {
            label: t('assessment.sepsisIndicators'),
            dataTestID: 'sepsisIndicators',
            options: sepsisIndicatorsOptions,
            action: setSepsisIndicators,
            listHeader: t('assessment.sepsisIndicators'),
            savedValues: sepsisIndicators,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AssessmentConstants.SET_POSSIBLE_SOURCE_OF_INFECTION,
          type: ProcedureConstants.TEXTAREA,
          hidden: _hideSepsisFields,
          props: {
            label: t('assessment.possibleSourceOfInfection'),
            dataTestID: 'possibleSourceOfInfection',
            onChangeAction: createTextHandler(setPossibleSourceOfInfection),
            value: possibleSourceOfInfection,
            maxlength: 255,
          },
        },
      ],
    },
  ]

  const setNumberPadOnChange = (values: string[], fieldId: number, fieldType: string, responseType: string) => {
    dispatch(setCustomFieldOption(values.join(''), fieldId, fieldType, responseType))
  }

  const setSingleSelectOnChange = (option: IOption, fieldId: number, fieldType: string, responseType: string) => {
    dispatch(setCustomFieldOption(option, fieldId, fieldType, responseType))
  }

  const setDateSelectOnChange = (value: string, fieldId: number, fieldType: string, responseType: string) => {
    dispatch(setCustomFieldOption(value, fieldId, fieldType, responseType))
  }

  const setMultiSelectOnChange = (fieldId: number, fieldType: string, responseType: string) => {
    return function (options: IOptions) {
      return setCustomFieldOption(options, fieldId, fieldType, responseType)
    }
  }

  const setTextOnChange = (e: CustomEvent, fieldId: number, fieldType: string, responseType: string) => {
    e.preventDefault()
    dispatch(setCustomFieldOption(e.detail.value, fieldId, fieldType, responseType))
  }

  const getDynamicInputType = (responseTypeText: string) => {
    switch (responseTypeText) {
      case 'Boolean - Yes/No':
      case 'Dropdown/Select List':
      case 'Service Roster':
        return ProcedureConstants.SINGLE_SELECT
      case 'Checkbox/Multiselect':
        return ProcedureConstants.MULTI_SELECT
      case 'Number':
        return ProcedureConstants.NUMBER_PAD
      case 'Date/Time':
        return ProcedureConstants.DATE
      default:
        return ProcedureConstants.TEXT
    }
  }

  const getDefaultValueForInputType = (inputType: string) => {
    switch (inputType) {
      case ProcedureConstants.SINGLE_SELECT:
        return {}
      case ProcedureConstants.MULTI_SELECT:
        return []
      case ProcedureConstants.NUMBER_PAD:
      case ProcedureConstants.TEXT:
      case ProcedureConstants.DATE:
        return ''
      default:
        return ''
    }
  }

  const getSelectedOptions = (inputType: string, fieldId: number) => {
    if (customAssessmentsFields?.length) {
      const foundValue = customAssessmentsFields.find((data) => data.fieldId === fieldId)
      return foundValue ? foundValue.value : getDefaultValueForInputType(inputType)
    }
    return getDefaultValueForInputType(inputType)
  }

  const setCustomAssessmentFields = useCallback(() => {
    const dynamicInputs = customActionFieldsOptions
      ?.filter((field: { actionId: number }) => field?.actionId === action?.id)
      .map((field: any) => {
        const type = getDynamicInputType(field.responseTypeText)
        const responseType = field.responseTypeText
        let fieldOptions = []

        if (field.responseTypeText === 'Service Roster') {
          fieldOptions = crewList
            ?.filter((crew: any) => crew?.userId)
            .map((crew: any) => ({
              id: crew.userId.id,
              name: crew.userId.name,
              responseType: responseType,
            }))
          fieldOptions.push({ id: -1, name: 'Other', responseType: field.responseTypeText })
        } else {
          fieldOptions = customActionFieldsAnswersOptions
            ?.filter((opt: IOption) => opt.fieldId === field.id)
            .map((opt: IOption) => ({
              id: opt.id,
              name: opt.name,
              responseType: responseType,
            }))
        }
        const preSelectedOption = getSelectedOptions(type, field.id)

        const baseInput = {
          key: AssessmentConstants.SET_CUSTOM_ASSESSMENTS_FIELDS,
          type,
          hidden: false,
          props: {
            label: field.fieldName,
            dataTestId: field.fieldName.replace(/\s+/g, ''),
            listHeader: field.fieldName,
          },
        }

        switch (type) {
          case ProcedureConstants.SINGLE_SELECT:
            return {
              key: randomNumber().toString(),
              inputs: [
                {
                  ...baseInput,
                  props: {
                    ...baseInput.props,
                    options: field.responseTypeText === 'Boolean - Yes/No' ? yesNoOptions : fieldOptions,
                    action: (option: IOption) =>
                      setSingleSelectOnChange(option, field.id, type, field.responseTypeText),
                    selectedOption: preSelectedOption,
                  },
                },
              ],
            }
          case ProcedureConstants.MULTI_SELECT:
            return {
              key: randomNumber().toString(),
              inputs: [
                {
                  ...baseInput,
                  props: {
                    ...baseInput.props,
                    options: field.responseTypeText === 'Boolean - Yes/No' ? yesNoOptions : fieldOptions,
                    action: setMultiSelectOnChange(field.id, type, field.responseTypeText),
                    savedValues: preSelectedOption,
                  },
                },
              ],
            }
          case ProcedureConstants.TEXT:
            return {
              key: randomNumber().toString(),
              inputs: [
                {
                  ...baseInput,
                  props: {
                    ...baseInput.props,
                    onChangeAction: (value: CustomEvent) =>
                      setTextOnChange(value, field.id, type, field.responseTypeText),
                    inputValue: preSelectedOption,
                  },
                },
              ],
            }
          case ProcedureConstants.NUMBER_PAD:
            return {
              key: randomNumber().toString(),
              inputs: [
                {
                  ...baseInput,
                  props: {
                    ...baseInput.props,
                    elementId: field.fieldName.replace(/\s+/g, ''),
                    values: (preSelectedOption as string)?.split('') || [],
                    onNumPadClick: (option: string[]) =>
                      setNumberPadOnChange(option, field.id, type, field.responseTypeText),
                    validCharacters: numberPadKeysWithDecimal,
                  },
                },
              ],
            }
          case ProcedureConstants.DATE:
            return {
              key: randomNumber().toString(),
              inputs: [
                {
                  ...baseInput,
                  props: {
                    ...baseInput.props,
                    selectedDateValue: preSelectedOption as string,
                    isShowIcon: true,
                    action: (value: string) => setDateSelectOnChange(value, field.id, type, field.responseTypeText),
                  },
                },
              ],
            }
          default:
            return null
        }
      })
      .filter(Boolean)
    return [
      ...staticCustomRows.filter(
        (row) => !row.inputs.some((input: any) => input.key === AssessmentConstants.SET_CUSTOM_ASSESSMENTS_FIELDS)
      ),
      ...dynamicInputs,
    ]
  }, [
    customAssessmentsFields,
    actionOptions,
    customActionFieldsOptions,
    customActionFieldsAnswersOptions,
    _hideNeuroFields,
    _hideSepsisFields,
  ])

  const inputRows = setCustomAssessmentFields()

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('assessment.assessment')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen color='light'>
        <Procedure
          type={'assessments'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
        />
      </IonContent>
    </IonPage>
  )
}

export default Assessment
